// @ts-nocheck
import React, { useMemo } from 'react';
import { Collapse, Badge, Empty } from 'antd';
import { colors, spacing } from '../design-system/tokens';
import { Typography } from '../design-system/typography';
import { SnippetItem } from './SnippetItem';
import { 
  Snippet, 
  SnippetSelection, 
  ShipmentContext 
} from './SmartSnippetBuilder';

const { Panel } = Collapse;

interface SnippetSelectorProps {
  applicableSnippets: Map<string, Snippet[]>;
  selectedSnippets: Map<string, SnippetSelection>;
  onSnippetChange: (snippetId: string, changes: Partial<SnippetSelection>) => void;
  shipmentContext: ShipmentContext;
  fieldOrder: readonly string[];
  fieldLabels: Record<string, string>;
}

export const SnippetSelector: React.FC<SnippetSelectorProps> = ({
  applicableSnippets,
  selectedSnippets,
  onSnippetChange,
  shipmentContext,
  fieldOrder,
  fieldLabels
}) => {
  // Group snippets by category within each field
  const groupedSnippets = useMemo(() => {
    const grouped = new Map<string, Map<string, Snippet[]>>();
    
    for (const [field, snippets] of applicableSnippets) {
      const categoryMap = new Map<string, Snippet[]>();
      
      for (const snippet of snippets) {
        const category = snippet.category || 'other';
        const categorySnippets = categoryMap.get(category) || [];
        categorySnippets.push(snippet);
        categoryMap.set(category, categorySnippets);
      }
      
      // Sort categories by priority
      const sortedCategories = new Map(
        Array.from(categoryMap.entries()).sort(([a], [b]) => {
          const priorityOrder: Record<string, number> = {
            'inspection': 1,
            'regulatory': 2,
            'warning': 3,
            'terminal': 4,
            'fee': 5,
            'appointment': 6,
            'photo': 7,
            'marking': 8,
            'other': 9
          };
          return (priorityOrder[a] || 9) - (priorityOrder[b] || 9);
        })
      );
      
      grouped.set(field, sortedCategories);
    }
    
    return grouped;
  }, [applicableSnippets]);

  // Calculate statistics for each field
  const getFieldStats = (field: string) => {
    const snippets = applicableSnippets.get(field) || [];
    const enabled = snippets.filter(s => selectedSnippets.get(s.id)?.enabled).length;
    const required = snippets.filter(s => s.required || selectedSnippets.get(s.id)?.enabled).length;
    const total = snippets.length;
    
    return { enabled, required, total };
  };

  // Get category display name and icon
  const getCategoryInfo = (category: string) => {
    const categoryMap: Record<string, { name: string; icon: string; color: string }> = {
      'inspection': { name: 'Container Inspection', icon: '🔍', color: colors.status.positive },
      'regulatory': { name: 'Regulatory Requirements', icon: '⚖️', color: colors.status.negative },
      'warning': { name: 'Port & Terminal Warnings', icon: '⚠️', color: colors.status.warning },
      'terminal': { name: 'Terminal Instructions', icon: '🏢', color: colors.primary[60] },
      'fee': { name: 'Carrier Fees & Charges', icon: '💰', color: colors.tertiary },
      'appointment': { name: 'Pickup & Scheduling', icon: '📅', color: colors.neutral[40] },
      'photo': { name: 'Photo Requirements', icon: '📸', color: colors.primary[40] },
      'marking': { name: 'Marking & Labels', icon: '🏷️', color: colors.secondary },
      'other': { name: 'Other Requirements', icon: '📄', color: colors.text.secondary }
    };
    
    return categoryMap[category] || categoryMap['other'];
  };

  // Get field icon and color
  const getFieldInfo = (field: string) => {
    const fieldMap: Record<string, { icon: string; color: string }> = {
      'shipmentNoteOne': { icon: '📦', color: colors.status.positive },
      'shipmentNoteTwo': { icon: '⚠️', color: colors.status.warning },
      'packingNotes': { icon: '📋', color: colors.primary[60] }
    };
    
    return fieldMap[field] || { icon: '📄', color: colors.text.secondary };
  };

  if (applicableSnippets.size === 0) {
    return (
      <div style={{ textAlign: 'center', padding: spacing['2xl'] }}>
        <Empty 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No applicable snippets found"
        />
      </div>
    );
  }

  return (
    <div style={{ height: '100%', overflow: 'auto' }}>
      <Collapse
        ghost
        size="small"
        defaultActiveKey={[...fieldOrder]}
        style={{ backgroundColor: 'transparent' }}
      >
        {fieldOrder.map(field => {
          const fieldSnippets = applicableSnippets.get(field) || [];
          const fieldCategories = groupedSnippets.get(field) || new Map();
          const stats = getFieldStats(field);
          const fieldInfo = getFieldInfo(field);
          
          if (fieldSnippets.length === 0) return null;

          return (
            <Panel
              key={field}
              header={
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                    <span style={{ fontSize: '16px' }}>{fieldInfo.icon}</span>
                    <Typography variant="h3" style={{ color: fieldInfo.color, margin: 0 }}>
                      {fieldLabels[field]}
                    </Typography>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                    <Badge 
                      count={stats.enabled} 
                      style={{ 
                        backgroundColor: fieldInfo.color,
                        fontSize: '10px'
                      }} 
                    />
                    <Typography 
                      variant="value" 
                      style={{ 
                        fontSize: '11px', 
                        color: colors.text.tertiary 
                      }}
                    >
                      {stats.total} available
                    </Typography>
                  </div>
                </div>
              }
              style={{
                borderBottom: `1px solid ${colors.border.default}`,
                backgroundColor: 'transparent'
              }}
            >
              <div style={{ paddingLeft: spacing.md }}>
                {Array.from(fieldCategories.entries()).map(([category, categorySnippets]) => {
                  const categoryInfo = getCategoryInfo(category);
                  const enabledInCategory = categorySnippets.filter(s => 
                    selectedSnippets.get(s.id)?.enabled
                  ).length;
                  
                  return (
                    <div key={category} style={{ marginBottom: spacing.lg }}>
                      {/* Category Header */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: spacing.sm,
                        marginBottom: spacing.md,
                        padding: spacing.sm,
                        backgroundColor: `${categoryInfo.color}10`,
                        borderRadius: '6px',
                        border: `1px solid ${categoryInfo.color}30`
                      }}>
                        <span style={{ fontSize: '14px' }}>{categoryInfo.icon}</span>
                        <Typography 
                          variant="label" 
                          style={{ 
                            color: categoryInfo.color, 
                            fontWeight: 600,
                            margin: 0,
                            flex: 1
                          }}
                        >
                          {categoryInfo.name}
                        </Typography>
                        {enabledInCategory > 0 && (
                          <Badge
                            count={enabledInCategory}
                            style={{
                              backgroundColor: categoryInfo.color,
                              fontSize: '10px'
                            }}
                          />
                        )}
                      </div>

                      {/* Category Snippets */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                        {categorySnippets.map(snippet => {
                          const selection = selectedSnippets.get(snippet.id) || {
                            snippetId: snippet.id,
                            enabled: false,
                            userModified: false
                          };
                          
                          return (
                            <SnippetItem
                              key={snippet.id}
                              snippet={snippet}
                              selection={selection}
                              onChange={(changes) => onSnippetChange(snippet.id, changes)}
                              shipmentContext={shipmentContext}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>
          );
        })}
      </Collapse>

      {/* Summary Footer */}
      <div style={{
        marginTop: spacing.xl,
        padding: spacing.md,
        backgroundColor: colors.background.tertiary,
        borderRadius: '8px',
        border: `1px solid ${colors.border.default}`
      }}>
        <Typography variant="label" style={{ marginBottom: spacing.xs }}>
          Selection Summary
        </Typography>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          {fieldOrder.map(field => {
            const stats = getFieldStats(field);
            const fieldInfo = getFieldInfo(field);
            
            if (stats.total === 0) return null;
            
            return (
              <div 
                key={field}
                style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center' 
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                  <span style={{ fontSize: '12px' }}>{fieldInfo.icon}</span>
                  <Typography variant="value" style={{ fontSize: '12px' }}>
                    {fieldLabels[field]}
                  </Typography>
                </div>
                <Typography 
                  variant="value" 
                  style={{ 
                    fontSize: '12px',
                    color: stats.enabled > 0 ? fieldInfo.color : colors.text.tertiary
                  }}
                >
                  {stats.enabled} / {stats.total}
                </Typography>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};